import { ROUTE_PATTERNS, WORKSPACE_SECTIONS, workspaceSectionPath } from "@/constants/routes";
import { DashboardShell } from "@/components/layout/dashboard-shell";
import type { BreadcrumbItem } from "@/components/layout/breadcrumbs";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { Button } from "@/components/ui/button";
import { useWorkspaces } from "@/features/workspaces/api";
import { useWorkspace, WorkspaceProvider } from "@/providers/workspace-provider";
import { Building2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

const SECTION_LABELS: Record<string, string> = {
  [WORKSPACE_SECTIONS.home]: "Overview",
  [WORKSPACE_SECTIONS.pos]: "POS",
  [WORKSPACE_SECTIONS.orders]: "Orders",
  [WORKSPACE_SECTIONS.products]: "Products",
  [WORKSPACE_SECTIONS.logs]: "Logs",
  [WORKSPACE_SECTIONS.users]: "Users",
  [WORKSPACE_SECTIONS.settings]: "Settings",
};

const WorkspaceChrome = ({ workspaceSlug, children }: { workspaceSlug: string; children: ReactNode }) => {
  const { workspace, role, scopes, workspaceId } = useWorkspace();
  const location = useLocation();

  const segments = location.pathname.split("/").filter(Boolean);
  const section = segments[2] ?? WORKSPACE_SECTIONS.home;
  const detail = segments[3];

  const breadcrumbs: BreadcrumbItem[] = [
    { label: workspace.name, to: workspaceSectionPath(workspaceSlug, "home") },
    { label: SECTION_LABELS[section] ?? "Section", to: workspaceSectionPath(workspaceSlug, "home") },
  ];
  if (detail) breadcrumbs.push({ label: detail.slice(0, 8) });

  return (
    <DashboardShell
      workspaceSlug={workspaceSlug}
      workspaceId={workspaceId}
      scopes={scopes}
      role={role}
      breadcrumbs={breadcrumbs}
    >
      {children}
    </DashboardShell>
  );
};

/**
 * `/w/:workspaceSlug/*` boundary: resolves the slug to a workspace the actor can
 * actually see, then provides the workspace context (settings, role, scopes) to
 * every nested route.
 */
export const WorkspaceLayout = () => {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const workspacesQuery = useWorkspaces();
  const workspace = workspacesQuery.data?.items.find((item) => item.slug === workspaceSlug);

  if (workspacesQuery.isLoading) return <LoadingState label="Loading workspace…" />;

  // Surface the API's message instead of a generic "not available": the most common
  // local failure is an unapplied schema, and the API already says exactly that.
  if (workspacesQuery.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-3">
          <ErrorState
            title="Could not load your workspaces"
            description={
              workspacesQuery.error instanceof Error
                ? workspacesQuery.error.message
                : "The API is unreachable."
            }
            onRetry={() => void workspacesQuery.refetch()}
          />
          <div className="flex justify-center">
            <Button asChild variant="outline" size="sm">
              <a href="/api/v1/health" target="_blank" rel="noreferrer noopener">
                Check API health
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!workspace || !workspaceSlug) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <EmptyState
          title="Workspace not available"
          description="Either it does not exist, or your account is not a member of it."
          icon={<Building2 className="size-8" aria-hidden />}
          action={
            <Button asChild>
              <Link to={ROUTE_PATTERNS.dashboard}>Back to workspaces</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <WorkspaceProvider workspaceId={workspace.id}>
      <WorkspaceChrome workspaceSlug={workspaceSlug}>
        <Outlet />
      </WorkspaceChrome>
    </WorkspaceProvider>
  );
};
